import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AddPlaceholder } from "@/components/design/AddPlaceholder";
import { TextInput } from "@/components/design/TextField";
import { isSearchable } from "@/lib/games/toplist/search";
import { cleanOwnTitle, OWN_TITLE_MAX_LENGTH } from "@/lib/games/toplist/snapshot";
import { ToplistCard, type ToplistItem } from "./ToplistCard";
import type { Genus } from "@/lib/games/toplist/genus";

// Where the search looks and what it may add, the same in every phase.
export interface ToplistSearchSource {
  // Entries whose names contain the query, one named exactly so first, and
  // whether there is one: then that is what was meant, not an own title.
  find: (query: string) => { hits: readonly ToplistItem[]; exact: boolean };
  // Says what is searched for, e.g. "Search cities".
  placeholder: string;
  // The grammar of what the list counts, for "Fehlt einer?" / "Fehlt eins?".
  genus: Genus;
  // Takes the query as an entry of the person's own.
  onTakeOwn: (title: string) => void;
}

// "Missing one?": the search over the whole domain, the same in the rounds and
// in the result. While it is in use its hits take the place of what the stage
// offers otherwise (`children`: the round, what was passed over). A tap on a hit
// takes it and clears the field. Hits already on the list show as such and
// cannot be taken twice; with every place filled none can be taken. What the
// catalogue lacks can be taken as typed, as an own entry, unless it names a hit
// exactly.
export function ToplistSearch({
  source,
  picked,
  full = false,
  onChoose,
  className = "",
  children,
}: {
  source: ToplistSearchSource;
  // Ids already on the list.
  picked: ReadonlySet<string>;
  full?: boolean;
  onChoose: (id: string) => void;
  className?: string;
  children: ReactNode;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const searching = isSearchable(query);
  const { hits, exact } = searching ? source.find(query) : { hits: [], exact: false };
  const ownTitle = searching && !exact && !full ? cleanOwnTitle(query) : null;

  const take = (pick: () => void) => {
    setQuery("");
    pick();
  };

  return (
    <div className={className}>
      <label className="block">
        <span className="smycles-field-label">
          <FormattedMessage id="grow.games.toplist.search.label" defaultMessage="Missing one?" values={{ genus: source.genus }} />
        </span>
        <TextInput
          type="search"
          className="mt-1"
          maxLength={OWN_TITLE_MAX_LENGTH}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={source.placeholder}
        />
      </label>
      {!searching ? (
        children
      ) : (
        <>
          {hits.length > 0 ? (
            <div className="mt-4 grid grid-cols-2 gap-3 @md:grid-cols-4">
              {hits.map((item) => (
                <ToplistCard
                  key={item.id}
                  item={item}
                  selected={picked.has(item.id)}
                  disabled={full || picked.has(item.id)}
                  onClick={() => take(() => onChoose(item.id))}
                />
              ))}
            </div>
          ) : (
            <p className="mt-4 smycles-field-meta text-ink-muted">
              <FormattedMessage id="grow.games.toplist.search.noMatch" defaultMessage="Nothing found." />
            </p>
          )}
          {ownTitle !== null ? (
            <AddPlaceholder
              className="mt-3"
              label={intl.formatMessage(
                { id: "grow.games.toplist.search.takeOwn", defaultMessage: "Take “{title}” as your own entry" },
                { title: ownTitle },
              )}
              onClick={() => take(() => source.onTakeOwn(ownTitle))}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
